import type { BillableFormPluginProps, SaveCallbackData } from '@domains/subscription/priv/Billables/usecases/BillableForm/plugin'
import type { MaxPrice } from '@domains/subscription/priv/Billables/usecases/Price/max/max'
import type { BaseAlgorithm } from '@domains/subscription/priv/components/Confirmable/confirmable'
import { Stack, Typography } from '@mui/material'
import { useRef } from 'react'
import { useForm } from 'react-hook-form'

export interface MaxPricePlugin<T extends BaseAlgorithm> {
  supports: (type: string) => boolean
  Component: React.ComponentType<MaxBillableFormProps<T>>
}

export interface MaxBillableFormProps<T extends BaseAlgorithm> extends BillableFormPluginProps {
  maxPrice: MaxPrice<T> | undefined
  onSubmit: (data: MaxPrice<T>) => Promise<void>
}

export const MaxPriceRow = ({
  maxPrice,
  onRegisterSave,
  onSubmit,
  additionalPlugins,
  templateId,
  unit,
}: MaxBillableFormProps<BaseAlgorithm>) => {
  const form = useForm<MaxPrice<BaseAlgorithm>>({
    values: maxPrice,
  })

  const allChangesRef = useRef<Record<string, SaveCallbackData>>({})

  const submitForm = async (values: MaxPrice<BaseAlgorithm>) => {
    await onSubmit(values).then(() => {
      onRegisterSave({
        changes: [],
        save: () => form.handleSubmit(submitForm)(),
      })
    })
  }

  const handleFieldChanges = (leftOrRight: 'left' | 'right', callback: SaveCallbackData) => {
    allChangesRef.current[leftOrRight] = callback

    const allChanges = Object.values(allChangesRef.current).flatMap((c) => c.changes)
    const callbacks = Object.values(allChangesRef.current).map((c) => c.save)

    onRegisterSave({
      changes: allChanges,
      save: () => {
        callbacks.forEach((save) => save())
        return form.handleSubmit(submitForm)()
      },
    })
  }

  const getPluginForAlgorithm = (algorithm: BaseAlgorithm | undefined): React.ComponentType<BillableFormPluginProps> => {
    if (!algorithm) {
      return () => (
        <Stack
          data-testid="missing-tier-plugin"
          sx={{
            gap: 2,
          }}
        >
          <Typography variant="p4Regular" sx={{ color: 'red' }}>
            Price does not exist for this billable
          </Typography>
        </Stack>
      )
    }

    const plugin = additionalPlugins?.find((plugin) => plugin.supports(algorithm))

    if (!plugin || !plugin.FormComponent) {
      return () => (
        <Stack
          data-testid="missing-tier-plugin"
          sx={{
            gap: 2,
          }}
        >
          <Typography variant="p4Regular" sx={{ color: 'red' }}>
            Backoffice does not support '{algorithm.type}' price types in this configuration
          </Typography>
        </Stack>
      )
    }

    return plugin.FormComponent
  }

  const LeftAlgorithm = getPluginForAlgorithm(maxPrice?.lalgorithm)
  const RightAlgorithm = getPluginForAlgorithm(maxPrice?.ralgorithm)

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <Typography variant="p3Regular">Left algorithm</Typography>
        <LeftAlgorithm
          price={maxPrice?.lalgorithm}
          additionalPlugins={additionalPlugins}
          templateId={templateId}
          unit={unit}
          onRegisterSave={(changes) => handleFieldChanges('left', changes)}
          onDataChange={(data) => form.setValue(`lalgorithm`, data)}
        />
      </Stack>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <Typography variant="p3Regular">Right algorithm</Typography>
        <RightAlgorithm
          price={maxPrice?.ralgorithm}
          additionalPlugins={additionalPlugins}
          templateId={templateId}
          unit={unit}
          onRegisterSave={(changes) => handleFieldChanges('right', changes)}
          onDataChange={(data) => form.setValue(`ralgorithm`, data)}
        />
      </Stack>
    </Stack>
  )
}

const noop = () => {}
